import { CHANNEL, TEMPLATE_TYPE } from '../../../../constants/messageTemplateKeys';
import { ERROR_REQUIRED, errorMaxChars } from '../../../../../../shared/constants/validationErrors';
import { isTriggerSectionRequired } from './sendConditionHelpers';

export const TEMPLATE_NAME_MAX_LEN = 300;
export const SUBJECT_MAX_LEN = 70;

export function hasBasicInformationErrors(errors) {
  return Object.values(errors ?? {}).some((value) => String(value ?? '').trim().length > 0);
}

export function hasBasicInformationDraftErrors(errors) {
  return (
    String(errors?.templateNameMaxError ?? '').trim().length > 0 ||
    String(errors?.templateNameRequiredError ?? '').trim().length > 0 ||
    String(errors?.triggerError ?? '').trim().length > 0 ||
    String(errors?.sendConditionsRequiredError ?? '').trim().length > 0
  );
}

// Blocking-level errors for the trigger section (the per-field display errors are
// built separately in buildSendConditionRowErrors). Both share the same
// "is the section required" rule via isTriggerSectionRequired.
function buildTriggerSectionErrors({
  type,
  triggerId,
  sendConditions,
  publishAttempt,
  draftAttempt,
  triggerTouched = true,
  sendConditionsTouched = null,
  // Per-row flag: Days may be left empty for rows where this is true (see computeDayOptionalByRow).
  dayOptionalByRow = [],
}) {
  const isTrigger = type === TEMPLATE_TYPE.TRIGGER;
  const required = isTriggerSectionRequired({
    isTrigger,
    triggerId,
    sendConditions,
    publishAttempt,
    draftAttempt,
  });

  if (!required) {
    return { triggerError: '', sendConditionsRequiredError: '' };
  }

  const rows = Array.isArray(sendConditions) ? sendConditions : [];
  const triggerError =
    triggerTouched && String(triggerId ?? '').trim().length === 0 ? ERROR_REQUIRED : '';

  // Pure blocking flag — never rendered (the per-field messages come from
  // buildSendConditionRowErrors). An empty field blocks save/publish, but only once it is
  // touched (a freshly-added or still-untouched field must not silently disable buttons).
  // Touched is tracked per field (`{ sendCondition, day }`); a null entry means "touched".
  const hasMissingField = rows.some((row, index) => {
    const rowTouched = sendConditionsTouched ? sendConditionsTouched[index] : null;
    const isConditionTouched = rowTouched ? Boolean(rowTouched.sendCondition) : true;
    const isDayTouched = rowTouched ? Boolean(rowTouched.day) : true;
    return (
      (isConditionTouched && String(row?.sendCondition ?? '').trim().length === 0) ||
      (!dayOptionalByRow[index] && isDayTouched && String(row?.day ?? '').trim().length === 0)
    );
  });
  const sendConditionsRequiredError = hasMissingField ? ERROR_REQUIRED : '';

  return { triggerError, sendConditionsRequiredError };
}

export function buildBasicInformationErrors({
  channel = CHANNEL.EMAIL,
  saveAttempt = 0,
  templateName = '',
  subjects = [],
  type = TEMPLATE_TYPE.MARKETING,
  selectedCampaignIds = [],
  triggerId = '',
  sendConditions = [],
  publishAttempt = saveAttempt > 0,
  draftAttempt = false,
  // Optional "touched" gating: when omitted (null), every field is treated as touched, so
  // the result validates everything (the contract the submit-time check relies on). When
  // provided, a required error is suppressed for an untouched field/row, so the button
  // states only reflect errors the user can actually see.
  touched = null,
  subjectsTouched = null,
  sendConditionsTouched = null,
  dayOptionalByRow = [],
}) {
  const isEmail = channel === CHANNEL.EMAIL;
  const isMarketing = type === TEMPLATE_TYPE.MARKETING;
  const trimmedTemplateName = String(templateName ?? '').trim();
  const templateNameLen = String(templateName ?? '').length;

  const isTemplateNameTouched = !touched || Boolean(touched.templateName);
  const isCampaignTouched = !touched || Boolean(touched.campaign);
  const isTriggerTouched = !touched || Boolean(touched.trigger);
  const isSubjectTouched = (index) => (subjectsTouched ? Boolean(subjectsTouched[index]) : true);

  const templateNameMaxError =
    templateNameLen > TEMPLATE_NAME_MAX_LEN ? errorMaxChars(TEMPLATE_NAME_MAX_LEN) : '';

  const templateNameRequiredError =
    saveAttempt > 0 && trimmedTemplateName.length === 0 && isTemplateNameTouched
      ? ERROR_REQUIRED
      : '';

  let subjectsError = '';
  if (isEmail && saveAttempt > 0) {
    const hasAny = subjects.some((s) => String(s ?? '').trim().length > 0);
    const hasTouchedEmpty = subjects.some(
      (s, index) => isSubjectTouched(index) && String(s ?? '').trim().length === 0,
    );
    if (!hasAny || hasTouchedEmpty) {
      subjectsError = ERROR_REQUIRED;
    }
  }

  const hasCampaignSelection =
    Array.isArray(selectedCampaignIds) &&
    selectedCampaignIds.some((id) => String(id ?? '').trim().length > 0);

  const campaignError =
    saveAttempt > 0 && isMarketing && !hasCampaignSelection && isCampaignTouched
      ? ERROR_REQUIRED
      : '';

  const { triggerError, sendConditionsRequiredError } = buildTriggerSectionErrors({
    type,
    triggerId,
    sendConditions,
    publishAttempt,
    draftAttempt,
    triggerTouched: isTriggerTouched,
    sendConditionsTouched,
    dayOptionalByRow,
  });

  return {
    templateNameMaxError,
    templateNameRequiredError,
    subjectsError,
    campaignError,
    triggerError,
    sendConditionsRequiredError,
  };
}
